import { useNodeProps } from '@/node-editor/hooks';
import { INode, INodeProps } from '@/types/node';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Icon,
  IconButton,
  Link,
  LinearProgress,
  Tooltip,
  Typography,
} from '@mui/material';
import axios from 'axios';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { IContent } from './contract';
import Select from 'react-select';
import { useDropzone } from 'react-dropzone';
import TagManagement from './tagManagement';
import { createUpload } from '@mux/upchunk';
import VideoDialog from '@/components/videoDialog';
import { fileSizeToHumanReadable, getFileIcon } from '@/lib/utils/format';
import { AuthContext } from '@/store';
import { commonToast } from '@/components/commonFunctions/commonToast';
import { Toast } from '@/components/commonFunctions/toastMessages';
import { deleteConfirmSwal } from '@/components/customModal/commonDialog/deleteConfirmSwal';
import { useTheme } from '@mui/material/styles';
import { useFileListsStyles } from './fileLists.styles';
interface INodeExtended extends Omit<INode, 'content'> {
  content: IContent;
}

interface IProps extends Omit<INodeProps, 'node'> {
  node: INodeExtended;
}

const STATUS = {
  TEMPORARY: 1,
  FILE: 2,
  ENCODING: 6,
  ERROR: 7,
};

interface VideoStatusPollerProps {
  file: any;
  reloadMediaList: () => void;
}

const VideoStatusPoller = ({
  file,
  reloadMediaList,
}: VideoStatusPollerProps): null => {
  const reloadRef = useRef(reloadMediaList);
  reloadRef.current = reloadMediaList;

  useEffect(() => {
    if (file.status === STATUS.FILE || file.status === STATUS.ERROR) return;

    const videoUploadStatus = () => {
      axios
        .get(`/api/media/${file.id}/videuploadstatus`)
        .then((response) => {
          if (response.data.status === 'finished') {
            reloadRef.current();
          }
        })
        .catch(() => {});
    };

    const interval = setInterval(videoUploadStatus, 4000);
    return () => clearInterval(interval);
  }, [file.id, file.status]);

  return null;
};

export const Render = ({
  node: {
    id,
    override,
    content: { order, reverse, shouldParticipantUpload, title },
  },
  conferenceId,
  pageId,
}: IProps) => {
  const theme = useTheme();
  const { authState, isModerator } = useContext(AuthContext);
  const [files, setFiles] = useState<any[]>([]);
  const [editTags, setEditTags] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [participantTags, setParticipantTags] = useState([]);
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const [selectedTags, setSelectedTags] = useState<any>(null);
  const [activeTag, setActiveTag] = useState<'all' | string | number>('all');

  const [videoProgress, setVideoProgress] = useState(0);
  const [error, setError] = useState(null);
  const videoUploadMediaId = useRef(null);
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [videoDialogData, setVideoDialogData] = useState<any>(null);
  const getConferenceUrl = (url_cname: any, name: any) => {
    const port = window.location.port ? `:${window.location.port}` : '';
    if (url_cname) {
      return `//${url_cname}`;
    } else {
      return `//${name}${window.location.hostname.substring(
        window.location.hostname.indexOf('.')
      )}${port}`;
    }
  };

  useEffect(() => {
    getFileList();
    getParticipantTagList();
  }, []);

  const getFileList = () => {
    axios
      .get(
        `/api/visitor/conferences/${conferenceId}/${pageId}/uploadedfiles?order=${
          order || 'date'
        }&reverse=${reverse ? '1' : '0'}`
      )
      .then((response) => {
        setFiles(response.data);
        setUploading(false);
      })
      .catch(() => {
        setFiles([]);
        setUploading(false);
      });
  };

  const getParticipantTagList = () => {
    axios
      .get(`/api/visitor/${conferenceId}/${pageId}/media/tags?participant=true`)
      .then((response) => {
        setParticipantTags(response.data);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const onDropFile = (acceptedFiles: File[]) => {
    const file = acceptedFiles?.[0];
    if (file) setSelectedFile(file);
  };

  const uploadStart = () => {
    setUploading(true);
    setVideoUploadMediaId(null);
    setVideoProgress(0);
    setError(null);
  };

  const setVideoUploadMediaId = (mediaId: string) => {
    videoUploadMediaId.current = mediaId;
  };

  const videoSigningFinished = (response: any) => {
    setVideoUploadMediaId(response.data.id);
  };

  const videoUploadProgress = (progress: any) => {
    setVideoProgress(Math.floor(progress.detail));
  };

  const setConversionStarted = (mediaId: string) => {
    axios
      .put(`/api/media/${mediaId}/setvideoconverting`)
      .then(() => {
        getFileList();
        setUploading(false);
        setSelectedFile(null);
        commonToast('success', Toast.uploadSuccess('File'));
      })
      .catch(() => {});
  };

  const videoUploadHandler = () => {
    setConversionStarted(videoUploadMediaId.current);
  };

  const uploadHandler = () => {
    getFileList();
    setSelectedFile(null);
    commonToast('success', Toast.uploadSuccess('File'));
  };

  const fileName = useMemo(() => selectedFile?.name ?? '', [selectedFile]);

  const truncateFileName = (name = '', maxLength = 21) => {
    if (name.length <= maxLength) return name;
    const dot = name.lastIndexOf('.');
    const ext = dot > 0 ? name.slice(dot) : '';
    return `${name.slice(0, Math.max(maxLength - ext.length - 1, 1))}…${ext}`;
  };

  const displayFileName = truncateFileName(fileName);

  const handleError = (error: any) => {
    console.log('DropZone handleError', error);
    commonToast('error', Toast.uploadError('File'));
    try {
      setError(error.response.data.message);
      setUploading(false);
    } catch (error) {
      console.log('DropZone handleError', error);
    }
  };

  const handleUpload = () => {
    if (selectedFile && selectedTags) {
      try {
        uploadStart();
        axios
          .post(
            `/api/visitor/conferences/${conferenceId}/uploadmediawithtags`,
            {
              encodedFileName: encodeURIComponent(selectedFile.name),
              fileName: selectedFile.name,
              fileType: selectedFile.type,
              fileSize: selectedFile.size,
              tags: [selectedTags.value],
            }
          )
          .then((response) => {
            if (selectedFile.type.includes('video')) {
              videoSigningFinished(response);
              const upload = createUpload({
                file: selectedFile,
                // Normally this would be retrieved via an API request to an endpoint
                // you control that would return an authenticated URL.
                endpoint: response.data.url,
                chunkSize: 5120, // Uploads the file in ~5mb chunks
              });

              upload.on('error', handleError);
              upload.on('progress', videoUploadProgress);
              upload.on('success', videoUploadHandler);
            } else {
              uploadFile(
                selectedFile,
                response.data.signedRequest,
                () => {
                  uploadHandler();
                },
                handleError
              );
            }
          })
          .catch(handleError);
      } catch (e) {
        console.log(e);
      }
    } else {
      if (!selectedTags)
        commonToast('warning', window.LANG.FILE_LIST_NO_TAGS_SELECTED);
      else if (!selectedFile)
        commonToast('warning', window.LANG.FILE_LIST_NO_FILE_SELECTED);
    }
  };

  const uploadFile = (
    file: any,
    signedRequest: any,
    callback: any,
    errorHandler: any
  ) => {
    axios
      .put(signedRequest, file)
      .then(() => {
        callback();
      })
      .catch(errorHandler);
  };

  const fileFormats = [
    '.jpeg',
    '.jpg',
    '.png',
    '.pdf',
    '.doc',
    '.docx',
    '.xls',
    '.xlsx',
    '.ppt',
    '.pptx',
    '.txt',
    '.mp4',
    '.mov',
  ];

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDrop: onDropFile,
    accept: fileFormats.join(','),
    multiple: false,
    noClick: true,
    noKeyboard: true,
    disabled: uploading,
  });

  const classes = useFileListsStyles(theme, { override, isDragActive }).render;

  const [checked, setChecked] = useState([]);

  const toggleChecked = (id: any, type: any) => {
    if (checked.find((c) => c.id === id)) {
      setChecked(checked.filter((c) => c.id !== id));
    } else {
      setChecked([...checked, { id, type }]);
    }
  };
  const openVideoDialog = (file: any) => {
    setVideoDialogData(file);
    setVideoDialogOpen(true);
  };

  const closeVideoDialog = () => {
    setVideoDialogOpen(false);
    setVideoDialogData(null);
  };

  const deleteMedia = (mediaIds: any, skipConfirm = false) => {
    if (!skipConfirm) {
      const confirmed = window.confirm('Really remove these images?');
      if (!confirmed) return;
    }
    const filemediaIds = mediaIds
      .filter((m: any) => m.type === 'file' || m.type === 'image')
      .map((m: any) => m.id);
    const videoIds = mediaIds
      .filter((m: any) => m.type === 'videomux')
      .map((m: any) => m.id);
    if (videoIds.length > 0) {
      axios
        .put(`/api/conferences/${conferenceId}/deletevideofiles`, {
          ids: videoIds,
        })
        .then(() => {
          getFileList();
          setChecked([]);
          commonToast('success', Toast.deleteSuccess('File'));
        })
        .catch(() => {
          commonToast('error', Toast.deleteError('File'));
        });
    }
    if (filemediaIds.length > 0) {
      axios
        .put(`/api/conferences/${conferenceId}/deletemediafiles`, {
          ids: filemediaIds,
        })
        .then((response) => {
          getFileList();
          setChecked([]);
          commonToast('success', Toast.deleteSuccess('File'));
        })
        .catch(() => {
          commonToast('error', Toast.deleteError('File'));
        });
    }
  };

  // Whether the current user may delete a given media file.
  const canDeleteMedia = (mediaFile: any) =>
    isModerator || mediaFile.userId == authState?.user?.id;

  const handleRowDelete = async (mediaFile: any) => {
    const isConfirmed = await deleteConfirmSwal({
      title: window.LANG.FILE_LIST_DELETE_FILE_TITLE,
      text: `Are you sure you want to delete "${mediaFile.original_filename}"?`,
    });
    if (!isConfirmed) return;
    deleteMedia([{ id: mediaFile.id, type: mediaFile.type }], true);
  };

  // Uppercased file extension, e.g. "PDF" / "PNG" / "MP4".
  const getFileTypeLabel = (mediaFile: any) => {
    const name = mediaFile.original_filename || '';
    const ext = name.includes('.') ? name.split('.').pop() : '';
    return ext ? ext.toUpperCase() : mediaFile.type?.toUpperCase() || 'FILE';
  };

  // YYYY-MM-DD date shown in the meta line.
  const formatDate = (value: any) => {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return `${value}`.slice(0, 10);
    return d.toISOString().slice(0, 10);
  };

  // Colour tint for the leading file-type icon.
  const getTypeTint = (mediaFile: any) => {
    if (mediaFile.type === 'image') return { fg: '#2fae87', bg: '#e6f6f0' };
    if (mediaFile.type === 'videomux') return { fg: '#e8963f', bg: '#fdf3e6' };
    const ext = (mediaFile.original_filename || '').split('.').pop() || '';
    if (ext.toLowerCase() === 'pdf') return { fg: '#e34c4c', bg: '#fdecec' };
    return { fg: '#5b6b7b', bg: '#eef1f4' };
  };

  const getDownloadUrl = (mediaFile: any) =>
    `${window.location.protocol}${getConferenceUrl(
      authState?.user?.conference?.url_cname,
      authState?.user?.conference?.name
    )}/downloadmediafile/${mediaFile.id}`;

  // Tabs: "All files" + one per tag group, each with its file count.
  const totalFileCount = useMemo(
    () =>
      files.reduce(
        (sum: number, group: any) => sum + (group.mediaFiles?.length || 0),
        0
      ),
    [files]
  );

  const visibleGroups = useMemo(
    () =>
      activeTag === 'all'
        ? files
        : files.filter((group: any) => group.tagId === activeTag),
    [files, activeTag]
  );

  // Media files the user may delete, within the current tab selection.
  const deletableVisibleFiles = useMemo(
    () =>
      visibleGroups
        .flatMap((group: any) => group.mediaFiles || [])
        .filter((m: any) => canDeleteMedia(m)),
    [visibleGroups, isModerator, authState?.user?.id]
  );

  const allVisibleSelected =
    deletableVisibleFiles.length > 0 &&
    deletableVisibleFiles.every((m: any) => checked.find((c) => c.id == m.id));

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      // Deselect the currently visible deletable files
      const visibleIds = new Set(deletableVisibleFiles.map((m: any) => m.id));
      setChecked(checked.filter((c) => !visibleIds.has(c.id)));
    } else {
      const existingIds = new Set(checked.map((c) => c.id));
      const additions = deletableVisibleFiles
        .filter((m: any) => !existingIds.has(m.id))
        .map((m: any) => ({ id: m.id, type: m.type }));
      setChecked([...checked, ...additions]);
    }
  };

  const handleBulkDelete = async () => {
    if (checked.length === 0) return;
    const isConfirmed = await deleteConfirmSwal({
      title: window.LANG.FILE_LIST_DELETE_FILES_TITLE,
      text: `Are you sure you want to delete ${checked.length} file${
        checked.length > 1 ? 's' : ''
      }?`,
    });
    if (!isConfirmed) return;
    deleteMedia(checked, true);
  };

  return (
    <Box sx={classes.root} {...useNodeProps(id)}>
      {authState.user ? (
        <>
          <Box sx={classes.headerCard}>
            <Box sx={classes.headerRow}>
              <Typography variant="h5" sx={classes.title}>
                {title}
              </Typography>
              {isModerator && (
                <Button
                  color="primary"
                  variant={editTags ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setEditTags(!editTags)}
                  startIcon={<Icon>sell</Icon>}
                  sx={classes.manageTagsButton}
                >
                  {window.LANG.FILE_LIST_MANAGE_TAGS}
                </Button>
              )}
            </Box>

            <Box display="flex" flexDirection="column">
              {isModerator && editTags && (
                <TagManagement
                  conferenceId={conferenceId}
                  isModerator={isModerator}
                  getParticipantTagList={getParticipantTagList}
                  getFileList={getFileList}
                  pageId={pageId}
                  onClose={() => setEditTags(false)}
                />
              )}
              {shouldParticipantUpload && (
                <Box sx={classes.uploadWrap}>
                  <Box {...getRootProps()} sx={classes.dropzone}>
                    <input {...getInputProps()} />
                    <Box sx={classes.dropzoneRow}>
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1.5}
                        sx={classes.dropzoneMain}
                      >
                        <Icon sx={classes.dropzoneIcon}>
                          {selectedFile ? getFileIcon(fileName) : 'upload'}
                        </Icon>
                        {uploading || selectedFile ? (
                          <Typography
                            variant="body2"
                            sx={classes.dropzoneFileName}
                            title={fileName}
                          >
                            {displayFileName}
                          </Typography>
                        ) : (
                          <Typography
                            variant="body2"
                            sx={classes.dropzonePromptText}
                          >
                            {isDragActive ? (
                              window.LANG.FILE_LIST_DROP_HERE
                            ) : (
                              <>
                                {window.LANG.FILE_LIST_DROP_PROMPT}{' '}
                                <Box
                                  component="span"
                                  onClick={open}
                                  sx={classes.dropzoneBrowse}
                                >
                                  {window.LANG.FILE_LIST_BROWSE}
                                </Box>
                              </>
                            )}
                          </Typography>
                        )}
                      </Box>

                      <Box sx={classes.dropzoneSelect}>
                        <Select
                          isClearable
                          options={participantTags}
                          placeholder={window.LANG.FILE_LIST_CHOOSE_TAGS}
                          value={selectedTags}
                          onChange={setSelectedTags}
                          styles={classes.select}
                        />
                      </Box>

                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleUpload}
                        disabled={uploading}
                        startIcon={
                          uploading ? (
                            <CircularProgress color="inherit" size={16} />
                          ) : undefined
                        }
                        sx={classes.uploadButton}
                      >
                        {uploading
                          ? `${window.LANG.FILE_LIST_UPLOADING_PROGRESS}`
                          : window.LANG.FILE_LIST_UPLOAD}
                      </Button>
                    </Box>

                    {uploading && (
                      <Box sx={classes.uploadProgressWrap}>
                        <LinearProgress
                          variant="determinate"
                          value={videoProgress}
                          sx={classes.uploadProgressBar}
                        />
                        <Box
                          component="span"
                          sx={classes.uploadProgressLabel}
                        >
                          {`${videoProgress}%`}
                        </Box>
                      </Box>
                    )}
                  </Box>

                  {error && (
                    <Typography
                      color="error"
                      variant="body2"
                      sx={classes.uploadError}
                    >
                      {error}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          <Box display="flex" flexDirection="column">
            {/* Toolbar row: filter tabs (left) + bulk actions (right) */}
            {files.length > 0 && (
              <Box sx={classes.toolbarRow}>
                <Box
                  sx={classes.filterTabList}
                  role="tablist"
                  aria-label="Filter files by tag"
                >
                  <Box
                    component="button"
                    role="tab"
                    aria-selected={activeTag === 'all'}
                    onClick={() => setActiveTag('all')}
                    sx={{
                      ...classes.filterTab,
                      ...(activeTag === 'all' ? classes.filterTabActive : {}),
                    }}
                  >
                    <Box component="span" sx={classes.filterTabLabel}>
                      {window.LANG.FILE_LIST_ALL_FILES}
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        ...classes.filterCount,
                        ...(activeTag === 'all'
                          ? classes.filterCountActive
                          : {}),
                      }}
                    >
                      {totalFileCount}
                    </Box>
                  </Box>
                  {files.map((group: any) => (
                    <Box
                      key={group.tagId}
                      component="button"
                      role="tab"
                      aria-selected={activeTag === group.tagId}
                      onClick={() => setActiveTag(group.tagId)}
                      sx={{
                        ...classes.filterTab,
                        ...(activeTag === group.tagId
                          ? classes.filterTabActive
                          : {}),
                      }}
                    >
                      <Box
                        component="span"
                        sx={classes.filterTabLabel}
                        title={group.tagName}
                      >
                        {group.tagName}
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          ...classes.filterCount,
                          ...(activeTag === group.tagId
                            ? classes.filterCountActive
                            : {}),
                        }}
                      >
                        {group.mediaFiles?.length || 0}
                      </Box>
                    </Box>
                  ))}
                </Box>

                {deletableVisibleFiles.length > 0 && (
                  <Box sx={classes.bulkActions}>
                    <FormControlLabel
                      sx={classes.selectAllControl}
                      control={
                        <Checkbox
                          size="small"
                          checked={allVisibleSelected}
                          indeterminate={
                            checked.length > 0 && !allVisibleSelected
                          }
                          onChange={toggleSelectAll}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={classes.selectAllLabel}>
                          {window.LANG.FILE_LIST_SELECT_ALL}
                        </Typography>
                      }
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<Icon fontSize="small">delete</Icon>}
                      onClick={handleBulkDelete}
                      disabled={checked.length === 0}
                      sx={classes.deleteSelectedButton}
                    >
                      {window.LANG.FILE_LIST_DELETE_SELECTED}
                      {checked.length > 0 ? ` (${checked.length})` : ''}
                    </Button>
                  </Box>
                )}
              </Box>
            )}

            {files.length === 0 && (
              <Box sx={classes.emptyState}>{window.LANG.FILE_LIST_EMPTY}</Box>
            )}

            {visibleGroups.map((group: any, groupIndex: number) => (
              <Box
                key={group.tagId}
                sx={groupIndex > 0 ? classes.groupBlock : {}}
              >
                {/* Group heading only shown in "All files" view */}
                {activeTag === 'all' && (
                  <Typography variant="subtitle1" sx={classes.groupHead}>
                    {group.tagName}
                  </Typography>
                )}

                {(!group.mediaFiles || group.mediaFiles.length === 0) && (
                  <Box sx={classes.emptyState}>
                    {window.LANG.FILE_LIST_EMPTY_TAG}
                  </Box>
                )}

                {group.mediaFiles?.map((mediaFile: any) => {
                  const tint = getTypeTint(mediaFile);
                  const isVideo = mediaFile.type === 'videomux';
                  const isEncoding =
                    isVideo &&
                    mediaFile.status !== STATUS.FILE &&
                    mediaFile.status !== STATUS.ERROR;
                  return (
                    <Box key={mediaFile.id} sx={classes.fileRow}>
                      {canDeleteMedia(mediaFile) && (
                        <Checkbox
                          size="small"
                          sx={classes.rowCheckbox}
                          checked={Boolean(
                            checked.find((c) => c.id == mediaFile.id)
                          )}
                          onChange={() =>
                            toggleChecked(mediaFile.id, mediaFile.type)
                          }
                        />
                      )}
                      <Box sx={classes.fileIconTinted(tint.fg, tint.bg)}>
                        <Icon fontSize="small">
                          {isVideo
                            ? 'play_arrow'
                            : mediaFile.type === 'image'
                            ? 'image'
                            : getFileIcon(mediaFile.original_filename)}
                        </Icon>
                      </Box>

                      <Box sx={classes.fileMain}>
                        <Typography
                          sx={classes.fileName}
                          title={mediaFile.original_filename}
                        >
                          {truncateFileName(mediaFile.original_filename, 40)}
                        </Typography>
                        <Box sx={classes.fileMeta}>
                          <span>{getFileTypeLabel(mediaFile)}</span>
                          <Box component="span" sx={classes.metaSep}>
                            ·
                          </Box>
                          <span>{fileSizeToHumanReadable(mediaFile.size)}</span>
                          <Box component="span" sx={classes.metaSep}>
                            ·
                          </Box>
                          <span>{formatDate(mediaFile.createdAt)}</span>
                          {(mediaFile.first_name || mediaFile.last_name) && (
                            <>
                              <Box component="span" sx={classes.metaSep}>
                                ·
                              </Box>
                              <Box component="span" sx={classes.metaUploader}>
                                {`${mediaFile.first_name || ''} ${
                                  mediaFile.last_name || ''
                                }`.trim()}
                              </Box>
                            </>
                          )}
                        </Box>
                      </Box>

                      <Box sx={classes.fileActions}>
                        {isEncoding && (
                          <VideoStatusPoller
                            file={mediaFile}
                            reloadMediaList={getFileList}
                          />
                        )}
                        {isVideo ? (
                          isEncoding ? (
                            <Tooltip
                              title={window.LANG.FILE_LIST_PROCESSING_VIDEO}
                            >
                              <Box
                                display="flex"
                                alignItems="center"
                                sx={classes.processingIndicator}
                              >
                                <CircularProgress size={16} color="inherit" />
                              </Box>
                            </Tooltip>
                          ) : (
                            <Tooltip title={window.LANG.FILE_LIST_PLAY}>
                              <IconButton
                                size="small"
                                onClick={() => openVideoDialog(mediaFile)}
                              >
                                <Icon fontSize="small">play_circle</Icon>
                              </IconButton>
                            </Tooltip>
                          )
                        ) : (
                          <Tooltip title={window.LANG.FILE_LIST_DOWNLOAD}>
                            <IconButton
                              size="small"
                              component={Link}
                              href={
                                mediaFile.type === 'image'
                                  ? `https://${process.env.STATIC_IMAGES}/conferences/${conferenceId}/${mediaFile.source_id}`
                                  : getDownloadUrl(mediaFile)
                              }
                              target="_blank"
                              rel="noreferrer"
                            >
                              <Icon fontSize="small">download</Icon>
                            </IconButton>
                          </Tooltip>
                        )}
                        {canDeleteMedia(mediaFile) && (
                          <Tooltip title={window.LANG.FILE_LIST_DELETE}>
                            <IconButton
                              size="small"
                              onClick={() => handleRowDelete(mediaFile)}
                              sx={classes.deleteAction}
                            >
                              <Icon fontSize="small">delete</Icon>
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ))}
          </Box>
          </Box>
          {videoDialogOpen && (
            <VideoDialog
              open={videoDialogOpen}
              onClose={closeVideoDialog}
              data={videoDialogData}
            />
          )}
        </>
      ) : (
        window.LANG.AUTH_REQUIRED
      )}
    </Box>
  );
};
